import bearing from '@turf/bearing';
import { CurveFlightIcon, WarningIcon } from '../icons';
import { CurveObj } from '../mapData';

export function loadSvgIconToMapbox(map: mapboxgl.Map, id: string, svgString: string) {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
  
    img.onload = () => {
      const width = img.width || 30;
      const height = img.height || 30;
  
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
  
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
  
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
  
      if (!map.hasImage(id)) {
        try {
          map.addImage(id, {
            width,
            height,
            data: ctx.getImageData(0, 0, width, height).data,
          }, { pixelRatio: 1 });
        } catch (e) {
          console.error("Add image failed:", e);
        }
      }
  
      URL.revokeObjectURL(url);
    };
  
    img.onerror = (e) => {
      console.error("Image load failed", e);
    };
  
    img.src = url;
}


export function CountIcon(count: number) {
  const displayCount = count > 999 ? "999+" : count.toString();
  const fontSize = displayCount.length > 3 ? 7 : 9;

  return `
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
    <circle cx="11" cy="11" r="9" fill="#121B26" />
    <text x="11" y="14" text-anchor="middle"
      font-size="${fontSize}"
      fill="#FFFFFF"
      font-family="Arial"
      font-weight="bold">
      ${displayCount}
    </text>
  </svg>`;
}
  
export function CurveLineLayer(mapIns: mapboxgl.Map, selectedObj: CurveObj) { 
    if (mapIns.hasImage('flight-icon')) {
        mapIns.removeImage('flight-icon');
    }
    if (mapIns.hasImage('flight-warning-icon')) {
        mapIns.removeImage('flight-warning-icon');
    }
    loadSvgIconToMapbox(mapIns, 'flight-icon', CurveFlightIcon(selectedObj.color));
    loadSvgIconToMapbox(mapIns, "flight-warning-icon", WarningIcon("#1C2633"));
    
    const cityA = selectedObj.start;
    const cityB = selectedObj.end;

    const curvedLineCoordinates = createCurve(cityA, cityB);

    const midpointIndex = Math.floor(curvedLineCoordinates.length / 2);

    const firstHalfCoords = curvedLineCoordinates.slice(0, midpointIndex + 1);

    const secondHalfCoords = curvedLineCoordinates.slice(midpointIndex);

    mapIns.addSource('line-solid', {
        type: 'geojson',
        data: {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: firstHalfCoords,
            },
            properties: {}
        },
    });

    const segmentCount = 5;
    const segments = splitLineIntoSegments(secondHalfCoords, segmentCount);

    const segmentsGeoJSON: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
        type: "FeatureCollection",
        features: segments.map((segmentCoords: [number, number][], index: number) => ({
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: segmentCoords,
            },
            properties: {
                segmentOpacity: 1 - (index / segmentCount) * 0.8,
            },
        })),
    };

    mapIns.addSource('line-dotted', {
        type: 'geojson',
        data: segmentsGeoJSON
    });

    mapIns.addLayer({
        id: 'line-solid-layer',
        type: 'line',
        source: 'line-solid',
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
        },
        paint: {
            'line-color': selectedObj.color,
            'line-width': 1,
            'line-opacity': 0.7,
        },
    });

    mapIns.addLayer({
        id: 'line-dotted-layer',
        type: 'line',
        source: 'line-dotted',
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
        },
        paint: {
            'line-color': selectedObj.color,
            'line-width': 1,
            'line-opacity': ['get', 'segmentOpacity'],
            'line-dasharray': [10, 10]
        },
    });

    mapIns.addSource('cities', {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: cityA },
                    properties: {}
                },
                {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: cityB },
                    properties: {}
                },
            ],
        },
    });

    mapIns.addLayer({
        id: 'cities-layer',
        type: 'circle',
        source: 'cities',
        paint: {
            'circle-radius': 3,
            'circle-color': selectedObj.color
        },
    });

    const midpointCoord = curvedLineCoordinates[midpointIndex];
    const flightBearing = getBearingAtIndex(curvedLineCoordinates, midpointIndex);

    mapIns.addSource('midpoint-icon', {
        type: 'geojson',
        data: {
            id: "navigate-flight-icon",
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: midpointCoord,
            },
            properties: {
                rotation: flightBearing - 65,
                icon1: selectedObj.isWarning ? 'flight-warning-icon' : ''
            },
        },
    });

    mapIns.addLayer({
        id: 'midpoint-icon-layer',
        type: 'symbol',
        source: 'midpoint-icon',
        layout: {
            'icon-image': 'flight-icon',
            'icon-size': 0.5,
            'icon-anchor': 'center',
            'icon-offset': [0, 0],
            'icon-rotate': ['get', 'rotation'],
            'icon-rotation-alignment': 'map',
            "icon-allow-overlap": true
        },
    });

    mapIns.addLayer({
        id: "midpoint-icon-layer-2",
        type: "symbol",
        source: "midpoint-icon",
        layout: {
          "icon-image": [
            "case",
            ["all", ["has", "icon1"], ["!=", ["get", "icon1"], ""]],
            ["get", "icon1"],
            ""
          ],
          "icon-size": 1,
          "icon-allow-overlap": true,
          "icon-offset": [
                [
                    "*",
                    15,
                    ["cos", ["/", ["*", ["+", ["get", "rotation"], -90], 3.141592653589793], 180]]
                ],
                [
                    "*",
                    15,
                    ["sin", ["/", ["*", ["+", ["get", "rotation"], -90], 3.141592653589793], 180]]
                ]
            ]
        },
    });
}

function createCurve(start: [number, number], end: [number, number], curvature = 0.1, points = 100): [number, number][] {
    const line: [number, number][] = [];

    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const dist = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    const perpendicularAngle = angle + Math.PI / 2;

    for (let i = 0; i <= points; i++) {
        const t = i / points;
        const x = start[0] + dx * t;
        const y = start[1] + dy * t;

        const direction = start[0] < end[0] ? 1 : -1;
        const offset = Math.sin(Math.PI * t) * curvature * dist * direction;

        const offsetX = x + offset * Math.cos(perpendicularAngle);
        const offsetY = y + offset * Math.sin(perpendicularAngle);

        line.push([offsetX, offsetY]);
    }
    return line;
}
  
function splitLineIntoSegments(coords: [number, number][], segmentCount: number) {
    const segments = [];
    const step = Math.floor(coords.length / segmentCount);

    for (let i = 0; i < segmentCount; i++) {

        const startIndex = i * step;

        const endIndex = (i === segmentCount - 1) ? coords.length : (startIndex + step + 1);

        const segmentCoords = coords.slice(startIndex, endIndex);
        segments.push(segmentCoords);
    }
    return segments;
}
  
function getBearingAtIndex(coords: [number, number][], index: number) {
    if (index <= 0) index = 1;
    if (index >= coords.length - 1) index = coords.length - 2;
    return bearing(coords[index], coords[index + 1]);
}

export function getBearing(from: [number, number], to: [number, number]) {
    const [lon1, lat1] = from.map(v => v * Math.PI / 180);
    const [lon2, lat2] = to.map(v => v * Math.PI / 180);
  
    const dLon = lon2 - lon1;
    const x = Math.sin(dLon) * Math.cos(lat2);
    const y = Math.cos(lat1) * Math.sin(lat2) -
              Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  
    const θ = Math.atan2(x, y) * 180 / Math.PI;
    return (θ + 360) % 360;
}